import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import {
    Article, ArrowLeft, CircleNotch, ArrowSquareOut,
    BookOpen, Users, Scales, Lightbulb, ChatCircleText, ListChecks,
    PencilLine, GraduationCap, Warning
} from "@phosphor-icons/react";

const SECTION_CONFIG = [
    { key: "1_background_context", label: "Background & Context", Icon: BookOpen },
    { key: "2_main_arguments", label: "Main Arguments", Icon: ChatCircleText },
    { key: "3_critical_analysis", label: "Critical Analysis", Icon: Scales },
    { key: "4_stakeholder_perspectives", label: "Stakeholder Perspectives", Icon: Users },
    { key: "5_solutions_reforms", label: "Solutions & Reforms", Icon: Lightbulb },
    { key: "6_facts_laws_schemes", label: "Facts · Laws · Schemes", Icon: ListChecks },
    { key: "7_answer_writing_framework", label: "Answer Writing Framework", Icon: PencilLine },
];

function humanKey(k) {
    return String(k).replace(/^\d+_/, "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// Non-recursive renderer: manually flattens 2 levels max, then dumps as text
function renderValue(v, depth = 0) {
    if (v == null) return null;
    if (typeof v === "string" || typeof v === "number") {
        return <p className="text-sm text-slate-700 leading-relaxed">{String(v)}</p>;
    }
    if (Array.isArray(v)) {
        return (
            <ul className="space-y-1.5">
                {v.map((item, i) => {
                    if (typeof item === "string" || typeof item === "number") {
                        return (
                            <li key={i} className="flex gap-2 text-sm text-slate-700 leading-relaxed">
                                <span className="text-[#1E3A8A] font-bold shrink-0">·</span>
                                <span>{String(item)}</span>
                            </li>
                        );
                    }
                    if (item && typeof item === "object") {
                        return (
                            <li key={i} className="flex gap-2 text-sm leading-relaxed">
                                <span className="text-[#1E3A8A] font-bold shrink-0">·</span>
                                <div className="flex-1 space-y-0.5">
                                    {Object.entries(item).map(([k, v2]) => (
                                        <div key={k}>
                                            <span className="font-semibold text-slate-900">{humanKey(k)}: </span>
                                            <span className="text-slate-700">{typeof v2 === "string" ? v2 : JSON.stringify(v2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </li>
                        );
                    }
                    return <li key={i}>{String(item)}</li>;
                })}
            </ul>
        );
    }
    if (typeof v === "object") {
        // Render each sub-key with mini-heading and its value
        return (
            <div className="space-y-4">
                {Object.entries(v).map(([k, val]) => {
                    if (depth >= 2) {
                        return (
                            <div key={k}>
                                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">{humanKey(k)}</div>
                                <p className="text-sm text-slate-700">{typeof val === "string" ? val : JSON.stringify(val)}</p>
                            </div>
                        );
                    }
                    return (
                        <div key={k}>
                            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">{humanKey(k)}</div>
                            {renderValue(val, depth + 1)}
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
}

export default function DailyEditorials() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/current-affairs/editorials", { timeout: 240000 });
                setData(data);
            } catch (e) {
                setError(e.response?.data?.detail || "Could not load today's editorials");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="min-h-[60vh] grid place-items-center">
                    <div className="text-center max-w-md">
                        <CircleNotch size={40} className="animate-spin text-[#1E3A8A] mx-auto mb-4" />
                        <div className="font-heading font-semibold text-lg">Analysing today's editorials…</div>
                        <div className="text-sm text-slate-500 mt-2">Fetching from The Hindu &amp; The New Indian Express, then Claude Sonnet 4.5 breaks each into a UPSC/KAS-ready study note. May take up to 2 minutes on the first request of the day.</div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !data?.editorials?.length) {
        return (
            <Layout>
                <button onClick={() => navigate(-1)} className="btn-ghost mb-6"><ArrowLeft size={16} /> Back</button>
                <div className="bento-card border-red-200 bg-red-50/40 text-red-800 text-sm">
                    <Warning size={20} weight="fill" className="inline mr-2" />
                    {error || "No editorials available today. Please retry in a few minutes."}
                </div>
            </Layout>
        );
    }

    const e = data.editorials[activeIdx];
    const sections = e.sections || {};

    return (
        <Layout>
            <button onClick={() => navigate(-1)} className="btn-ghost mb-6" data-testid="editorials-back">
                <ArrowLeft size={16} /> Back
            </button>

            <div className="mb-8 text-center">
                <div className="inline-flex items-center gap-2 rounded-full border-2 border-indigo-200 bg-indigo-50/70 px-3 py-1 text-xs font-semibold text-indigo-800 mb-3">
                    <Article size={14} weight="fill" /> {data.display_date || data.date} · {data.editorials.length} editorial{data.editorials.length === 1 ? "" : "s"}
                </div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl">Daily Editorials</h1>
                <p className="text-slate-500 mt-2 text-sm">Structured UPSC/KAS study notes from today's The Hindu &amp; The New Indian Express editorials.</p>
            </div>

            <div className="flex flex-wrap gap-2 justify-center mb-6">
                {data.editorials.map((ed, i) => (
                    <button
                        key={ed.editorial_id}
                        onClick={() => setActiveIdx(i)}
                        data-testid={`editorial-tab-${i}`}
                        className={`px-3.5 py-2 rounded-full text-xs font-semibold border-2 transition-colors ${
                            i === activeIdx ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[#BFD3FF] text-slate-700 hover:border-[#1E3A8A]"
                        }`}
                    >
                        {ed.source_paper}{ed.source_section ? ` · ${ed.source_section}` : ""}
                    </button>
                ))}
            </div>

            <div className="space-y-5">
                <div className="bento-card">
                    <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-bold">{e.source_paper} · {e.publish_date}</div>
                            <h2 className="font-heading font-bold text-2xl mt-1 text-slate-900 leading-tight">{e.title}</h2>
                            {e.summary && <p className="text-slate-700 mt-3 leading-relaxed">{e.summary}</p>}
                        </div>
                        <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs shrink-0">
                            <ArrowSquareOut size={14} /> Read original
                        </a>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {(e.gs_paper_relevance || []).map((gs) => (
                            <span key={gs} className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border-2 border-[#1E3A8A]/60 text-[#1E3A8A]">
                                {gs}
                            </span>
                        ))}
                        {(e.tags || []).map((t) => (
                            <span key={t} className="text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                                {t}
                            </span>
                        ))}
                    </div>
                </div>

                {SECTION_CONFIG.map(({ key, label, Icon }) => {
                    const sec = sections[key];
                    if (!sec) return null;
                    return (
                        <div key={key} className="bento-card">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A]"><Icon size={20} weight="duotone" /></div>
                                <h3 className="font-heading font-bold text-lg text-slate-900">{label}</h3>
                            </div>
                            {renderValue(sec)}
                        </div>
                    );
                })}

                {(e.mains_questions || []).length > 0 && (
                    <div className="bento-card">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-amber-100 text-amber-800"><PencilLine size={20} weight="duotone" /></div>
                            <h3 className="font-heading font-bold text-lg text-slate-900">Practice Mains Questions</h3>
                        </div>
                        <div className="space-y-3">
                            {e.mains_questions.map((q, i) => (
                                <div key={i} className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50/40">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-1.5">
                                        <span>{q.paper}</span>
                                        <span>·</span>
                                        <span className="font-mono">{q.marks} marks</span>
                                    </div>
                                    <div className="text-slate-900 font-medium">{q.question}</div>
                                    {q.approach_hint && <div className="text-xs text-slate-600 mt-2 italic">Approach: {q.approach_hint}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {(e.prelims_themes || []).length > 0 && (
                    <div className="bento-card">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-800"><GraduationCap size={20} weight="duotone" /></div>
                            <h3 className="font-heading font-bold text-lg text-slate-900">Prelims Themes to Revise</h3>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {e.prelims_themes.map((p, i) => (
                                <div key={i} className="p-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50/40">
                                    <div className="font-semibold text-slate-900">{p.theme}</div>
                                    <div className="text-xs text-slate-600 mt-1">{p.what_to_study}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
